import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * AMANAH LITE MODE — §12 basic-phone / low-bandwidth mode.
 *
 * When active, the app:
 *   • Skips autoplay video, framer-motion animations, and hero
 *     illustrations (via `data-lite="on"` on the `<html>` element).
 *   • Hides bandwidth-heavy poster galleries and video shelves.
 *   • Prefers system fonts.
 *
 * Detection priority (highest first):
 *   1. Explicit `?lite=1` query param (or `?lite=0` to force off).
 *   2. Explicit preference in `localStorage.amanah_lite = 'on' | 'off' | 'auto'`.
 *   3. Auto-detected slow network via `navigator.connection`
 *      (`saveData === true`, or `effectiveType === '2g' | 'slow-2g'`).
 *
 * Rule: never override an explicit user preference. Auto-detection only
 * kicks in when the preference is 'auto' (the default).
 *
 * Any component can read the boolean with `useLiteMode()`.
 */
const LiteModeContext = createContext({ lite: false, pref: 'auto', setPref: () => {} });
const STORAGE_KEY = 'amanah_lite';

const detectSlowNetwork = () => {
  if (typeof navigator === 'undefined') return false;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  if (c.saveData === true) return true;
  const type = c.effectiveType;
  return type === 'slow-2g' || type === '2g';
};

const readPref = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'on' || v === 'off' || v === 'auto') return v;
  } catch { /* ignore */ }
  return 'auto';
};

const readQueryOverride = () => {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search).get('lite');
  if (p === '1' || p === 'on') return 'on';
  if (p === '0' || p === 'off') return 'off';
  return null;
};

export const LiteModeProvider = ({ children }) => {
  const [pref, setPrefState] = useState(readPref);
  const [autoLite, setAutoLite] = useState(detectSlowNetwork);

  // Watch for connection changes at runtime.
  useEffect(() => {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return undefined;
    const handler = () => setAutoLite(detectSlowNetwork());
    c.addEventListener?.('change', handler);
    return () => c.removeEventListener?.('change', handler);
  }, []);

  // Query param override — once, on mount.
  useEffect(() => {
    const q = readQueryOverride();
    if (q) {
      setPrefState(q);
      try { localStorage.setItem(STORAGE_KEY, q); } catch { /* ignore */ }
    }
  }, []);

  // Compute effective lite state.
  const lite = pref === 'on' || (pref === 'auto' && autoLite);

  // Reflect on <html> so CSS + third-party heavy components can hook in.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-lite', lite ? 'on' : 'off');
  }, [lite]);

  const setPref = (next) => {
    setPrefState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  };

  return (
    <LiteModeContext.Provider value={{ lite, pref, setPref, autoDetected: autoLite }}>
      {children}
    </LiteModeContext.Provider>
  );
};

export const useLiteMode = () => useContext(LiteModeContext);
